import AnualidadSnapshot from "../types/AnualidadSnapshot";

export interface CambioDetectado {
  campo: string;
  valorCongelado: string | number | boolean;
  valorActual: string | number | boolean;
  mensaje: string;
}

/**
 * Detecta y genera mensajes de cambios entre el snapshot y los valores actuales
 * @param snapshot - Objeto AnualidadSnapshot con datos congelados y actuales
 * @returns Array de mensajes describiendo cada cambio detectado
 */
export function detectarCambiosAnualidad(
  snapshot: AnualidadSnapshot
): CambioDetectado[] {
  const cambios: CambioDetectado[] = [];

  // Si no hay snapshot, no hay comparación posible
  if (snapshot.sinSnapshot) {
    return cambios;
  }

  // Función auxiliar para agregar cambios
  const agregarCambio = (
    campo: string,
    valorCongelado: any,
    valorActual: any,
    etiqueta: string
  ) => {
    if (
      valorCongelado !== valorActual &&
      valorCongelado != null &&
      valorActual != null
    ) {
      cambios.push({
        campo,
        valorCongelado,
        valorActual,
        mensaje: `${etiqueta}: cambió de "${valorCongelado}" a "${valorActual}"${
          snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
        }`
      });
    }
  };

  // Comparar estatus
  agregarCambio(
    "estatus",
    snapshot.estatusSnapshot,
    snapshot.estatus,
    "Estatus"
  );

  // Comparar descripción
  agregarCambio(
    "descripcion",
    snapshot.descripcionSnapshot,
    snapshot.descripcion,
    "Descripción"
  );

  // Comparar vendedor
  if (snapshot.idVendedorSnapshot !== snapshot.idVendedor) {
    cambios.push({
      campo: "vendedor",
      valorCongelado: `${snapshot.vendedorSnapshot || "N/A"} (ID: ${
        snapshot.idVendedorSnapshot
      })`,
      valorActual: `${snapshot.vendedor || "N/A"} (ID: ${snapshot.idVendedor})`,
      mensaje: `Se detectó un cambio de vendedor: de "${snapshot.vendedorSnapshot ||
        "N/A"}" a "${snapshot.vendedor || "N/A"}"${
        snapshot.razonCambioVendedor
          ? ` - Razón: ${snapshot.razonCambioVendedor}`
          : ""
      }${snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""}`
    });
  }

  // Comparar membresía socio
  if (snapshot.idMembresiaSocioSnapshot !== snapshot.idMembresiaSocio) {
    cambios.push({
      campo: "idMembresiaSocio",
      valorCongelado: snapshot.idMembresiaSocioSnapshot,
      valorActual: snapshot.idMembresiaSocio,
      mensaje: `La anualidad cambió de membresía (ID: ${
        snapshot.idMembresiaSocioSnapshot
      } → ${snapshot.idMembresiaSocio})${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar público y club base (traspaso de membresía)
  if (
    snapshot.idPublicoSnapshot !== snapshot.idPublico ||
    (snapshot.idClubBaseSnapshot || 0) !== (snapshot.idClubBase || 0)
  ) {
    cambios.push({
      campo: "traspasoMembresia",
      valorCongelado: `${snapshot.clubBaseSnapshot ||
        "N/A"} (${snapshot.clubBaseClaveSnapshot || ""}) - Público: ${
        snapshot.idPublicoSnapshot
      }`,
      valorActual: `${snapshot.clubBase || "N/A"} (${snapshot.clubBaseClave ||
        ""}) - Público: ${snapshot.idPublico}`,
      mensaje: `Se detectó un traspaso de membresía: de "${
        snapshot.clubBaseSnapshot
      }" a "${snapshot.clubBase}"${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar promoción
  if (snapshot.idPromocionSnapshot !== snapshot.idPromocion) {
    cambios.push({
      campo: "promocion",
      valorCongelado: snapshot.promocionSnapshot || "Sin promoción",
      valorActual: snapshot.promocion || "Sin promoción",
      mensaje: `Promoción: cambió de "${snapshot.promocionSnapshot ||
        "Sin promoción"}" a "${snapshot.promocion || "Sin promoción"}"${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar venta
  if (snapshot.idVentaSnapshot !== snapshot.idVenta) {
    cambios.push({
      campo: "idVenta",
      valorCongelado: snapshot.idVentaSnapshot,
      valorActual: snapshot.idVenta,
      mensaje: `Se detectó un cambio en la venta (ID: ${
        snapshot.idVentaSnapshot
      } → ${snapshot.idVenta})${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar club facturación
  if (snapshot.idClubFacturacionSnapshot !== snapshot.idClubFacturacion) {
    cambios.push({
      campo: "clubFacturacion",
      valorCongelado: `${snapshot.clubFacturacionSnapshot ||
        "N/A"} (${snapshot.clubFacturacionClaveSnapshot || ""})`,
      valorActual: `${snapshot.clubFacturacion ||
        "N/A"} (${snapshot.clubFacturacionClave || ""})`,
      mensaje: `Club Facturación: cambió de "${
        snapshot.clubFacturacionSnapshot
      }" a "${snapshot.clubFacturacion}"${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar folio factura
  if ((snapshot.folioFacturaSnapshot || "") !== (snapshot.folioFactura || "")) {
    cambios.push({
      campo: "folioFactura",
      valorCongelado: snapshot.folioFacturaSnapshot || "Sin folio",
      valorActual: snapshot.folioFactura || "Sin folio",
      mensaje: `Cambio en el folio de factura: de "${snapshot.folioFacturaSnapshot ||
        "Sin folio"}" a "${snapshot.folioFactura || "Sin folio"}"${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar estatus factura (normalizar TIMBRADA y ENVIADA_ERP)
  const estatusSnapshotNormalizado =
    snapshot.facturaEstatusSnapshot === "TIMBRADA" ||
    snapshot.facturaEstatusSnapshot === "ENVIADA_ERP"
      ? "TIMBRADA"
      : snapshot.facturaEstatusSnapshot;
  const estatusActualNormalizado =
    snapshot.facturaEstatus === "TIMBRADA" ||
    snapshot.facturaEstatus === "ENVIADA_ERP"
      ? "TIMBRADA"
      : snapshot.facturaEstatus;

  if (estatusSnapshotNormalizado !== estatusActualNormalizado) {
    cambios.push({
      campo: "facturaEstatus",
      valorCongelado: snapshot.facturaEstatusSnapshot,
      valorActual: snapshot.facturaEstatus,
      mensaje: `Estatus Factura: cambió de "${
        snapshot.facturaEstatusSnapshot
      }" a "${snapshot.facturaEstatus}"${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Comparar nota de crédito
  if (
    snapshot.facturaConNotaCreditoSnapshot !== snapshot.facturaConNotaCredito
  ) {
    cambios.push({
      campo: "facturaConNotaCredito",
      valorCongelado: snapshot.facturaConNotaCreditoSnapshot ? "Sí" : "No",
      valorActual: snapshot.facturaConNotaCredito ? "Sí" : "No",
      mensaje: snapshot.facturaConNotaCredito
        ? `Se aplicó una nota de crédito${
            snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
          }`
        : `Se removió la nota de crédito${
            snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
          }`
    });
  }

  // Comparar validez de factura
  if (snapshot.facturaValidaSnapshot !== snapshot.facturaValida) {
    cambios.push({
      campo: "facturaValida",
      valorCongelado: snapshot.facturaValidaSnapshot ? "Válida" : "No válida",
      valorActual: snapshot.facturaValida ? "Válida" : "No válida",
      mensaje: !snapshot.facturaValida
        ? `La factura ya no es válida${
            snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
          }`
        : `La factura ahora es válida${
            snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
          }`
    });
  }

  // Comparar fecha de finalización
  agregarCambio(
    "fechaFinalizacion",
    snapshot.fechaFinalizacionSnapshot,
    snapshot.fechaFinalizacion,
    "Fecha Finalización"
  );

  // Comparar precio de venta
  if (snapshot.precioVentaSnapshot !== snapshot.precioVenta) {
    const precioSnapshotStr =
      snapshot.precioVentaSnapshot != null
        ? snapshot.precioVentaSnapshot.toFixed(2)
        : "0.00";
    const precioActualStr =
      snapshot.precioVenta != null ? snapshot.precioVenta.toFixed(2) : "0.00";
    cambios.push({
      campo: "precioVenta",
      valorCongelado: snapshot.precioVentaSnapshot,
      valorActual: snapshot.precioVenta,
      mensaje: `Se detectó un cambio de precio de venta: de $${precioSnapshotStr} a $${precioActualStr}${
        snapshot.evaluadorModifico ? " (actualizado por evaluador)" : ""
      }`
    });
  }

  // Detectar cambio de precio adicional (de la tabla anualidad_precio)
  if (
    snapshot.cambioPrecio &&
    snapshot.precioAnterior &&
    snapshot.precioNuevoCambio
  ) {
    const precioAnteriorStr = snapshot.precioAnterior.toFixed(2);
    const precioNuevoStr = snapshot.precioNuevoCambio.toFixed(2);
    const fechaStr = snapshot.fechaCambioPrecio
      ? `Fecha: ${snapshot.fechaCambioPrecio}`
      : "";
    cambios.push({
      campo: "cambioPrecio",
      valorCongelado: snapshot.precioAnterior,
      valorActual: snapshot.precioNuevoCambio,
      mensaje: `CAMBIO DE PRECIO DETECTADO: $${precioAnteriorStr} → $${precioNuevoStr} (${fechaStr}) - Posible práctica desleal`
    });
  }

  // Agregar mensaje si el evaluador modificó la anualidad
  if (snapshot.evaluadorModifico && cambios.length > 0) {
    cambios.unshift({
      campo: "evaluador",
      valorCongelado: "",
      valorActual: "",
      mensaje: `Esta anualidad fue actualizada por un evaluador${
        snapshot.fechaActualizacion ? ` el ${snapshot.fechaActualizacion}` : ""
      }`
    });
  }

  // Agregar mensaje si la cotización fue eliminada
  if (snapshot.cotizacionEliminada) {
    cambios.unshift({
      campo: "eliminada",
      valorCongelado: "",
      valorActual: "",
      mensaje: "Esta cotización fue eliminada del sistema"
    });
  }

  return cambios;
}

/**
 * Genera un resumen de texto de los cambios detectados
 * @param snapshot - Objeto AnualidadSnapshot
 * @returns String con el resumen de cambios
 */
export function generarResumenCambios(snapshot: AnualidadSnapshot): string {
  const cambios = detectarCambiosAnualidad(snapshot);

  if (cambios.length === 0) {
    return "No se detectaron cambios";
  }

  return cambios.map(c => c.mensaje).join("\n");
}

/**
 * Obtiene solo los campos que cambiaron
 * @param snapshot - Objeto AnualidadSnapshot
 * @returns Array de nombres de campos que cambiaron
 */
export function obtenerCamposCambiados(snapshot: AnualidadSnapshot): string[] {
  const cambios = detectarCambiosAnualidad(snapshot);
  return cambios.map(c => c.campo);
}
